import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import LineItemPriceField from '@/ui/components/addEditItemSidebar/LineItemPriceField';
import { Card, Conditional, SwitchCard } from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';

import { FC } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod/v4';

export const LINE_ITEM_PRICE_FORM_SCHEMA = z.object({
  predictedMaterialCost: z.number().optional(),
  markupTotals: z.object({
    markupType: z.enum(['profitPercentage', 'profitAmount', 'price']),
    profitPercentage: z.number(),
    profitAmount: z.number(),
    price: z.number(),
  }),
  autoUpdatePrice: z.boolean(),
});

export type TLineItemPriceCardFormState = z.infer<
  typeof LINE_ITEM_PRICE_FORM_SCHEMA
>;

type TProps = {
  hasLinkedMaterials: boolean;
  readonlyMarkupValues?: boolean;
};

const LineItemPriceCard: FC<TProps> = (props) => {
  const { hasLinkedMaterials, readonlyMarkupValues } = props;

  /**
   * Queries
   */
  const { data: regionPreferences, isLoading: isLoadingRegionPreferences } =
    useGetMyRegionPreferences();

  const formMethods = useFormContext<TLineItemPriceCardFormState>();

  const markupType = useWatch({
    control: formMethods.control,
    name: 'markupTotals.markupType',
  });
  const predictedMaterialCost = useWatch({
    control: formMethods.control,
    name: 'predictedMaterialCost',
  });

  if (isLoadingRegionPreferences || !regionPreferences) {
    return null;
  }

  return (
    <>
      <Conditional condition={hasLinkedMaterials}>
        <Card>
          <Card.Body>
            <div className="flex justify-between">
              <label>Materials (ex tax)</label>

              <p>
                <CurrencyValue
                  value={predictedMaterialCost || 0}
                  currency={regionPreferences.currency}
                />
              </p>
            </div>
          </Card.Body>
        </Card>
      </Conditional>

      <Card>
        <Card.Body>
          <Controller
            control={formMethods.control}
            name="markupTotals"
            render={({ field: { onChange, value } }) => {
              return (
                <LineItemPriceField
                  markupTotals={value}
                  hasLinkedMaterials={hasLinkedMaterials}
                  predictedMaterialCost={predictedMaterialCost}
                  onMarkupTotalChange={onChange}
                  readonlyMarkupValues={readonlyMarkupValues}
                  currency={regionPreferences.currency}
                />
              );
            }}
          />
        </Card.Body>
      </Card>

      <Conditional condition={markupType !== 'price'}>
        <Controller
          name="autoUpdatePrice"
          control={formMethods.control}
          render={({ field: { onChange, value } }) => {
            return (
              <SwitchCard
                label={
                  <div>
                    <h4>Auto update</h4>

                    <p>When material costs change update my sale price</p>
                  </div>
                }
                value={value}
                onChange={onChange}
              />
            );
          }}
        />
      </Conditional>
    </>
  );
};

export default LineItemPriceCard;
